import { z } from "zod";
import {
  optionalEmail,
  optionalEnum,
  optionalUrl,
  optionalUuid,
} from "./_helpers/optional";
import { ExternalIdEntrySchema } from "./external-id.schema";

// === Unresolved Fields (ADV-1101, ADR-0043 Option D) ===
//
// Per-contact capture for raw imported values the lenient pre-pass nulled
// (e.g. `n/a@gmail.com`). The shape mirrors the jsonb stored on
// `core.contacts.unresolved_fields` — one entry per field, latest capture
// wins, cleared when the rep resolves the field via PATCH /core/contacts/:id
// (Phase 3). Settles ADR-0043's deferred storage-shape question.
export const UnresolvedFieldEntrySchema = z.object({
  raw_value: z.string().describe("Raw Value"),
  reason: z.string().describe("Reason"),
  import_job_id: z.string().uuid().nullable().describe("Source Import Job"),
  captured_at: z.string().datetime().describe("Captured At"),
});
export type UnresolvedFieldEntry = z.infer<typeof UnresolvedFieldEntrySchema>;

export const UnresolvedFieldsSchema = z
  .record(z.string(), UnresolvedFieldEntrySchema)
  .nullable()
  .optional();
export type UnresolvedFields = z.infer<typeof UnresolvedFieldsSchema>;

// === Core Contact ===
//
// ADV-1101: `email` is intentionally NOT `.email()`-validated on the read
// schema. The DB CHECK (ADV-1088) is `email IS NULL OR email <> ''` — format
// enforcement is owned by the import boundary (`import-lenient-fields.ts`)
// and by PATCH input schemas (`UpdateContactInputSchema` in
// `apps/api/src/modules/core/routes.ts`). Loosening here lets a "keep as-is"
// resolve of a junk-looking address (`n/a@gmail.com`) round-trip through the
// API without the read parser stripping the value.

export const ContactSchema = z.object({
  id: z.string().uuid().describe("ID"),
  email: z.string().nullable().describe("Email"),
  phone: z.string().nullable().describe("Phone"),
  phone_normalized: z.string().nullable().describe("Phone Normalized"),
  first_name: z.string().nullable().describe("First Name"),
  last_name: z.string().nullable().describe("Last Name"),
  company_name: z.string().nullable().describe("Company Name"),
  contact_type: z
    .enum(["person", "company", "organization"])
    .default("person")
    .describe("Contact Type"),
  share_data_across_network: z.boolean().describe("Share Data Across Network"),
  consent_given_at: z.string().nullable().describe("Consent Given At"),
  unresolved_fields: UnresolvedFieldsSchema.describe("Unresolved Fields"),
  custom_fields: z.record(z.unknown()).default({}).describe("Custom Fields"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});
export type Contact = z.infer<typeof ContactSchema>;

export const ContactInsertSchema = ContactSchema.omit({
  id: true,
  phone_normalized: true,
  created_at: true,
  updated_at: true,
}).partial({
  share_data_across_network: true,
  consent_given_at: true,
});

// === Contact-Account Membership ===

export const ContactAccountMembershipSchema = z.object({
  id: z.string().uuid().describe("ID"),
  contact_id: z.string().uuid().describe("Contact"),
  account_id: z.string().uuid().describe("Account"),
  is_primary_account: z.boolean().describe("Primary Account"),
  matched_at: z.string().nullable().describe("Matched At"),
  match_source: z.string().nullable().describe("Match Source"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});
export type ContactAccountMembership = z.infer<
  typeof ContactAccountMembershipSchema
>;

// === Contact Relationships ===

export const contactRelationshipTypes = [
  "spouse",
  "partner",
  "site_contact",
  "billing_contact",
  "decision_maker",
  "property_manager",
  "other",
  // ADR-0052: partner people (stable org, churning people) reuse
  // contact_relationships with these trade-partner-oriented types.
  "sales_contact",
  "logistics_contact",
  "support_contact",
  "account_manager",
] as const;

export const ContactRelationshipSchema = z.object({
  id: z.string().uuid().describe("ID"),
  primary_contact_id: z.string().uuid().describe("Primary Contact"),
  related_contact_id: z.string().uuid().describe("Related Contact"),
  relationship_type: z
    .enum(contactRelationshipTypes)
    .describe("Relationship Type"),
  business_id: z.string().uuid().nullable().describe("Business"),
  notes: z.string().nullable().describe("Notes"),
  is_active: z.boolean().describe("Active"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});
export type ContactRelationship = z.infer<typeof ContactRelationshipSchema>;

// === Contact Roles ===

// ADR-0052: `vendor` was generalized into `trade_partner` (one contact can be
// both a customer and a trade partner for the same business).
export const roleTypes = ["customer", "team_member", "trade_partner"] as const;

export const ContactRoleSchema = z.object({
  id: z.string().uuid().describe("ID"),
  contact_id: z.string().uuid().describe("Contact"),
  business_id: z.string().uuid().describe("Business"),
  role_type: z.enum(roleTypes).describe("Role Type"),
  is_active: z.boolean().describe("Active"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});
export type ContactRole = z.infer<typeof ContactRoleSchema>;

// === Customer Attributes ===

export const lifecycleStatuses = [
  "lead",
  "prospect",
  "customer",
  "former_customer",
  "inactive",
] as const;

export const CustomerAttributesSchema = z.object({
  id: z.string().uuid().describe("ID"),
  contact_role_id: z.string().uuid().describe("Contact Role"),
  lifecycle_status: z.enum(lifecycleStatuses).describe("Lifecycle Status"),
  territory_id: z.string().uuid().nullable().describe("Territory"),
  assigned_rep_id: z.string().uuid().nullable().describe("Assigned Rep"),
  lead_source: z.string().nullable().describe("Lead Source"),
  external_crm_id: z.string().nullable().describe("External CRM ID"),
  customer_number: z.string().nullable().describe("Customer Number"),
  customer_type: z
    .enum(["residential", "commercial"])
    .nullable()
    .describe("Customer Type"),
  brand_id: z.string().uuid().nullable().describe("Brand"),
  notes: z.string().nullable().describe("Notes"),
  tags: z.array(z.string()).nullable().describe("Tags"),
  custom_fields: z.record(z.unknown()).nullable().describe("Custom Fields"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});
export type CustomerAttributes = z.infer<typeof CustomerAttributesSchema>;

// === Team Member Attributes ===

export const teamMemberRoles = [
  "admin",
  "manager",
  "sales_rep",
  "production",
  "finance",
] as const;

export const TeamMemberAttributesSchema = z.object({
  id: z.string().uuid().describe("ID"),
  contact_role_id: z.string().uuid().describe("Contact Role"),
  user_id: z.string().nullable().describe("User"),
  display_title: z.string().nullable().describe("Display Title"),
  commission_tier: z.string().nullable().describe("Commission Tier"),
  role: z.enum(teamMemberRoles).nullable().describe("Role"),
  primary_territory_id: z
    .string()
    .uuid()
    .nullable()
    .describe("Primary Territory"),
  headshot_url: z.string().nullable().describe("Headshot URL"),
  settings: z.record(z.unknown()).describe("Settings"),
  custom_fields: z
    .record(z.unknown())
    .nullable()
    .optional()
    .describe("Custom Fields"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});
export type TeamMemberAttributes = z.infer<typeof TeamMemberAttributesSchema>;

// === Brand Assignments ===

export const BrandAssignmentSchema = z.object({
  id: z.string().uuid().describe("ID"),
  contact_role_id: z.string().uuid().describe("Contact Role"),
  brand_id: z.string().uuid().describe("Brand"),
  is_active: z.boolean().describe("Active"),
  can_create_proposals: z.boolean().describe("Can Create Proposals"),
  can_view_products: z.boolean().describe("Can View Products"),
  created_at: z.string().describe("Created At"),
});
export type BrandAssignment = z.infer<typeof BrandAssignmentSchema>;

// === Team Member Business Assignments ===

export const TeamMemberBusinessAssignmentSchema = z.object({
  id: z.string().uuid().describe("ID"),
  contact_role_id: z.string().uuid().describe("Contact Role"),
  business_id: z.string().uuid().describe("Business"),
  primary_territory_id: z
    .string()
    .uuid()
    .nullable()
    .describe("Primary Territory"),
  is_active: z.boolean().describe("Active"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
});
export type TeamMemberBusinessAssignment = z.infer<
  typeof TeamMemberBusinessAssignmentSchema
>;

// === View Types (flat projections from DB views) ===

export const ContactWithRolesSchema = z.object({
  contact_id: z.string().uuid().describe("Contact"),
  email: z.string().nullable().describe("Email"),
  phone: z.string().nullable().describe("Phone"),
  first_name: z.string().nullable().describe("First Name"),
  last_name: z.string().nullable().describe("Last Name"),
  company_name: z.string().nullable().describe("Company Name"),
  unresolved_fields: UnresolvedFieldsSchema.describe("Unresolved Fields"),
  contact_role_id: z.string().uuid().describe("Contact Role"),
  business_id: z.string().uuid().describe("Business"),
  business_name: z.string().describe("Business Name"),
  role_type: z.enum(roleTypes).describe("Role Type"),
  is_active: z.boolean().describe("Active"),
  role_created_at: z.string().describe("Role Created At"),
});
export type ContactWithRoles = z.infer<typeof ContactWithRolesSchema>;

export const TeamMemberUnifiedSchema = z.object({
  team_member_attributes_id: z
    .string()
    .uuid()
    .describe("Team Member Attributes"),
  contact_id: z.string().uuid().describe("Contact"),
  contact_role_id: z.string().uuid().describe("Contact Role"),
  business_id: z.string().uuid().describe("Business"),
  account_id: z.string().uuid().describe("Account"),
  email: z.string().email().nullable().describe("Email"),
  phone: z.string().nullable().describe("Phone"),
  first_name: z.string().nullable().describe("First Name"),
  last_name: z.string().nullable().describe("Last Name"),
  display_title: z.string().nullable().describe("Display Title"),
  commission_tier: z.string().nullable().describe("Commission Tier"),
  role: z.enum(teamMemberRoles).nullable().describe("Role"),
  territory_id: z.string().uuid().nullable().describe("Territory"),
  headshot_url: z.string().nullable().describe("Headshot URL"),
  user_id: z.string().nullable().describe("User"),
  is_active: z.boolean().describe("Active"),
  created_at: z.string().describe("Created At"),
  updated_at: z.string().describe("Updated At"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});
export type TeamMemberUnified = z.infer<typeof TeamMemberUnifiedSchema>;

export const OtherBusinessMembershipSchema = z.object({
  business_id: z.string().uuid().describe("Business"),
  business_name: z.string().describe("Business Name"),
  business_slug: z.string().describe("Business Slug"),
  role_type: z.enum(roleTypes).describe("Role Type"),
  contact_role_id: z.string().uuid().describe("Contact Role"),
  can_access: z.boolean().describe("Can Access"),
});
export type OtherBusinessMembership = z.infer<
  typeof OtherBusinessMembershipSchema
>;

export const CustomerUnifiedSchema = z.object({
  contact_id: z.string().uuid().describe("Contact"),
  email: z.string().email().nullable().describe("Email"),
  phone: z.string().nullable().describe("Phone"),
  first_name: z.string().nullable().describe("First Name"),
  last_name: z.string().nullable().describe("Last Name"),
  company_name: z.string().nullable().describe("Company Name"),
  contact_role_id: z.string().uuid().describe("Contact Role"),
  business_id: z.string().uuid().describe("Business"),
  is_active: z.boolean().describe("Active"),
  customer_attributes_id: z.string().uuid().describe("Customer Attributes"),
  lifecycle_status: z.enum(lifecycleStatuses).describe("Lifecycle Status"),
  territory_id: z.string().uuid().nullable().describe("Territory"),
  assigned_rep_id: z.string().uuid().nullable().describe("Assigned Rep"),
  lead_source: z.string().nullable().describe("Lead Source"),
  customer_number: z.string().nullable().describe("Customer Number"),
  customer_type: z
    .enum(["residential", "commercial"])
    .nullable()
    .describe("Customer Type"),
  brand_id: z.string().uuid().nullable().describe("Brand"),
  notes: z.string().nullable().describe("Notes"),
  tags: z.array(z.string()).nullable().describe("Tags"),
  custom_fields: z.record(z.unknown()).nullable().describe("Custom Fields"),
  external_crm_id: z.string().nullable().describe("External CRM ID"),
  created_at: z.string().nullable().describe("Created At"),
  updated_at: z.string().nullable().describe("Updated At"),
  opportunity_count: z.number().int().default(0).describe("Opportunity Count"),
  salesperson_user_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Salesperson"),
  assigned_rep_name: z.string().nullable().optional().describe("Sales Rep"),
  other_business_memberships: z
    .array(OtherBusinessMembershipSchema)
    .default([])
    .describe("Other Business Memberships"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});
export type CustomerUnified = z.infer<typeof CustomerUnifiedSchema>;

// === Customer Write Schemas (domain-oriented, not table-mirroring) ===

export const CreateCustomerInputSchema = z.object({
  // Contact identity
  first_name: z.string().optional().describe("First Name"),
  last_name: z.string().optional().describe("Last Name"),
  email: optionalEmail().describe("Email"),
  phone: z.string().optional().describe("Phone"),
  company_name: z.string().optional().describe("Company Name"),
  // Customer attributes
  lifecycle_status: z
    .enum(lifecycleStatuses)
    .default("lead")
    .describe("Lifecycle Status"),
  customer_type: optionalEnum(["residential", "commercial"] as const).describe(
    "Customer Type",
  ),
  territory_id: optionalUuid().describe("Territory"),
  assigned_rep_id: optionalUuid().describe("Assigned Rep"),
  lead_source: z.string().optional().describe("Lead Source"),
  customer_number: z.string().optional().describe("Customer Number"),
  notes: z.string().optional().describe("Notes"),
  tags: z.array(z.string()).optional().describe("Tags"),
  custom_fields: z
    .record(z.string(), z.unknown())
    .optional()
    .describe("Custom Fields"),
});
export type CreateCustomerInput = z.infer<typeof CreateCustomerInputSchema>;

export const UpdateCustomerInputSchema = CreateCustomerInputSchema.partial();
export type UpdateCustomerInput = z.infer<typeof UpdateCustomerInputSchema>;

// === Contact Write Schemas (ADV-1101) ===
//
// PATCH /core/contacts/:id edits the contact-identity surface (email, phone,
// name, company) directly — distinct from PATCH /core/customers/:id which
// requires a customer role and writes customer_attributes. The dual-display
// "Fix" flow on the contact-detail page targets this endpoint so the rep can
// resolve an unresolved imported value (or keep it verbatim) without
// indirecting through the customer surface.

export const UpdateContactInputSchema = z
  .object({
    // Email here is intentionally strict (`optionalEmail()`) — the loose
    // read-side `ContactSchema.email` exists to round-trip a "keep as-is"
    // raw value that the import boundary already nulled. Manual edits go
    // through this schema and must validate.
    email: optionalEmail().describe("Email"),
    phone: z.string().nullable().optional().describe("Phone"),
    first_name: z.string().nullable().optional().describe("First Name"),
    last_name: z.string().nullable().optional().describe("Last Name"),
    company_name: z.string().nullable().optional().describe("Company Name"),
  })
  .strict()
  .describe("Update Contact Input");
export type UpdateContactInput = z.infer<typeof UpdateContactInputSchema>;

// POST /core/contacts/:id/resolve-field — the "keep as-is" action. Promotes
// the raw_value verbatim onto the clean column and clears the unresolved
// entry. v1 supports email + phone only (the two lenient-pre-pass fields).
export const ResolveContactFieldInputSchema = z
  .object({
    field: z.enum(["email", "phone"]).describe("Field"),
    action: z.literal("keep_raw").describe("Action"),
  })
  .strict()
  .describe("Resolve Contact Field Input");
export type ResolveContactFieldInput = z.infer<
  typeof ResolveContactFieldInputSchema
>;

// === Team Member Write Schemas ===

export const CreateTeamMemberInputSchema = z.object({
  // Contact identity
  email: z.string().email().describe("Email"),
  first_name: z.string().optional().describe("First Name"),
  last_name: z.string().optional().describe("Last Name"),
  phone: z.string().optional().describe("Phone"),
  // Team member attributes
  role: z.enum(teamMemberRoles).describe("Role"),
  commission_tier: z.string().optional().describe("Commission Tier"),
  display_title: z.string().optional().describe("Display Title"),
  primary_territory_id: optionalUuid().describe("Primary Territory"),
  headshot_url: optionalUrl().describe("Headshot URL"),
  settings: z.record(z.string(), z.unknown()).optional().describe("Settings"),
  // ADV-897: import-settable active flag. Defaults true so existing callers
  // (single-create, attach, integration transformers) keep their current
  // behavior. Lives on core.contact_roles.is_active per research; sibling
  // is_active flags on team_member_business_assignments / brand_assignments
  // are out of scope.
  is_active: z.boolean().default(true).describe("Active"),
});
export type CreateTeamMemberInput = z.infer<typeof CreateTeamMemberInputSchema>;

// ADV-897: `is_active` is omitted from the update schema. `updateTeamMember`
// would silently no-op on it (the bulk_update RPC doesn't carry it), and
// accepting a field the service ignores misleads API consumers. Use
// `deleteTeamMember` / `bulkDeactivateTeamMembers` for the deactivation
// path; threading is_active through update is tracked as a follow-up.
export const UpdateTeamMemberInputSchema = CreateTeamMemberInputSchema.omit({
  is_active: true,
}).partial();
export type UpdateTeamMemberInput = z.infer<typeof UpdateTeamMemberInputSchema>;

// === Search ===

export const ContactMembershipSchema = z.object({
  business_id: z.string().uuid().describe("Business"),
  business_name: z.string().describe("Business Name"),
  role_type: z.enum(roleTypes).describe("Role Type"),
  contact_role_id: z.string().uuid().describe("Contact Role"),
});
export type ContactMembership = z.infer<typeof ContactMembershipSchema>;

export const ContactSearchResultSchema = z.object({
  contact_id: z.string().uuid().describe("Contact"),
  first_name: z.string().nullable().describe("First Name"),
  last_name: z.string().nullable().describe("Last Name"),
  email: z.string().nullable().describe("Email"),
  phone: z.string().nullable().describe("Phone"),
  company_name: z.string().nullable().describe("Company Name"),
  memberships: z.array(ContactMembershipSchema).describe("Memberships"),
  has_current_business_role: z.boolean().describe("Has Current Business Role"),
});
export type ContactSearchResult = z.infer<typeof ContactSearchResultSchema>;
